import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Smartphone, Plus, RefreshCw, Send, Inbox, Clock, QrCode, Star, Copy, Check, HelpCircle, Download } from 'lucide-react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import apiClient from '../lib/apiClient';
import { useAuth } from '../contexts/AuthContext';
import { 
  initSmsWebSocket, 
  subscribeToDeviceStatusChanged, 
  unsubscribeFromDeviceStatusChanged,
  disconnectSmsWebSocket 
} from '../lib/smsWebsocketService';

interface SmsDevice {
  id: number;
  deviceId: string;
  deviceName: string;
  phoneNumber: string;
  isActive: boolean;
  isPaired?: boolean;
  fcmToken?: string;
  platform?: string;
  lastSeen?: string;
  createdAt: string;
}

interface PairingInfo {
  code: string;
  qrCode: string; // Data URL for QR image
  expiresAt: string;
  backendUrl: string;
}

interface SmsMessage {
  id: string;
  recipient: string;
  content: string;
  status: 'pending' | 'sent' | 'delivered' | 'failed';
  direction: 'outbound' | 'inbound';
  bitrixMessageId?: string;
  createdAt: string;
  sentAt?: string;
  deliveredAt?: string;
}

export default function SmsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [devices, setDevices] = useState<SmsDevice[]>([]);
  const [messages, setMessages] = useState<SmsMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDevice, setSelectedDevice] = useState<SmsDevice | null>(null);
  const [showAddDevice, setShowAddDevice] = useState(false);
  const [pairingInfo, setPairingInfo] = useState<PairingInfo | null>(null);
  const [copiedCode, setCopiedCode] = useState(false);
  // const [defaultDeviceId, setDefaultDeviceId] = useState<number | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalMessages, setTotalMessages] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // New device form state
  const [newDevice, setNewDevice] = useState({
    deviceName: '',
    phoneNumber: '',
  });

  useEffect(() => {
    loadDevices();
    loadMessages();

    let cancelled = false;

    // Suscribirse a cambios de estado de dispositivos
    const handleDeviceStatusChanged = (data: {
      deviceId: number;
      userId: string;
      isActive: boolean;
      isPaired: boolean;
      deviceName: string;
    }) => {
      console.log('[SMS] Device status changed:', data);
      console.log('[SMS] Current user:', { userId: user?.id, userIdString: user?.id?.toString() });
      
      // Solo procesar si el evento es del usuario actual
      const currentUserId = user?.id?.toString();
      if (data.userId !== currentUserId) {
        console.log('[SMS] Event ignored: different user', { 
          eventUserId: data.userId, 
          eventUserIdType: typeof data.userId,
          currentUserId,
          currentUserIdType: typeof currentUserId,
          matches: data.userId === currentUserId 
        });
        return;
      }
      
      console.log('[SMS] Processing event for current user');
      
      // Actualizar el dispositivo en la lista
      setDevices(prevDevices => {
        const existingDevice = prevDevices.find(d => d.id === data.deviceId);
        
        if (existingDevice) {
          // Actualizar dispositivo existente
          return prevDevices.map(device => 
            device.id === data.deviceId
              ? { ...device, isActive: data.isActive, isPaired: data.isPaired }
              : device
          );
        } else {
          // Dispositivo nuevo (recién emparejado), recargar lista completa
          loadDevices();
          return prevDevices;
        }
      });

      // Si el dispositivo se emparejó (isActive && isPaired), cerrar modal de QR
      if (data.isActive && data.isPaired) {
        console.log('[SMS] Device paired successfully, closing QR modal');
        console.log('[SMS] Device details:', { 
          deviceId: data.deviceId, 
          isActive: data.isActive, 
          isPaired: data.isPaired,
          deviceName: data.deviceName 
        });
        setShowAddDevice(false);
        setPairingInfo(null);
        setNewDevice({ deviceName: '', phoneNumber: '' });
        
        // Recargar dispositivos para asegurar que la UI esté actualizada
        loadDevices();
      } else {
        console.log('[SMS] Device status changed but not fully paired:', {
          isActive: data.isActive,
          isPaired: data.isPaired
        });
      }
    };

    // Inicializar WebSocket (ahora fetchea el token antes de conectar) y
    // suscribirse una vez conectado. Envuelto en un IIFE async porque un
    // efecto de React no puede ser async directamente.
    (async () => {
      await initSmsWebSocket();
      if (cancelled) return;
      await subscribeToDeviceStatusChanged(handleDeviceStatusChanged);
    })();

    // Cleanup al desmontar el componente
    return () => {
      cancelled = true;
      unsubscribeFromDeviceStatusChanged(handleDeviceStatusChanged);
      disconnectSmsWebSocket();
    };
  }, []);

  // Verificar si ya hay cualquier dispositivo registrado
  const hasAnyDevice = devices.length > 0;

  const loadDevices = async () => {
    try {
      const response = await apiClient.get('/sms/devices');
      console.log('[SMS] loadDevices response:', response);
      // apiClient interceptor ya extrae response.data, así que response ES el objeto con { success, devices }
      setDevices(Array.isArray(response.devices) ? response.devices : []);
    } catch (error) {
      console.error('Error loading devices:', error);
      setDevices([]);
    }
  };

  const loadMessages = async (page: number = currentPage) => {
    try {
      setLoading(true);
      
      // Construir parámetros de query
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (searchPhone) params.append('phone', searchPhone);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      
      const response = await apiClient.get(`/sms/messages?${params.toString()}`);
      
      // Asumiendo que el backend devuelve: { data: [], total: number, page: number, totalPages: number }
      setMessages(Array.isArray(response.data) ? response.data : []);
      setTotalMessages(response.total || 0);
      setTotalPages(response.totalPages || 1);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1); // Reset to first page on new search
    loadMessages(1);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchPhone('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
    loadMessages(1);
  };

  const registerDevice = async () => {
    try {
      const response = await apiClient.post('/sms/devices/register', newDevice);
      console.log('[SMS] registerDevice response:', response);
      
      // apiClient interceptor ya extrae response.data, así que response ES el objeto con { success, device, pairingInfo }
      if (response.success) {
        if (response.pairingInfo) {
          setPairingInfo(response.pairingInfo);
        }
        
        // Add device to list if provided
        if (response.device) {
          setDevices([...devices, response.device]);
        }
      }
      
      // Don't close the form yet - show QR code for pairing
    } catch (error: any) {
      console.error('Error registering device:', error);
      const errorMessage = error.response?.data?.error || error.message || t('failedToRegisterDevice');
      alert(errorMessage);
    }
  };

  const closeAddDeviceModal = () => {
    setShowAddDevice(false);
    setPairingInfo(null);
    setNewDevice({ deviceName: '', phoneNumber: '' });
  };

  const copyPairingCode = () => {
    if (pairingInfo) {
      navigator.clipboard.writeText(pairingInfo.code);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  // const setAsDefaultDevice = async (deviceId: number) => {
  //   try {
  //     await apiClient.post(`/sms/devices/${deviceId}/set-default`);
  //     setDefaultDeviceId(deviceId);
  //     alert(t('deviceSetAsDefault'));
  //     await loadDevices(); // Refresh to see updated status
  //   } catch (error) {
  //     console.error('Error setting default device:', error);
  //     alert(t('failedToSetDefaultDevice'));
  //   }
  // };

  const deactivateDevice = async (deviceId: number) => {
    if (!confirm(t('confirmDeactivateDevice'))) return;
    
    try {
      await apiClient.delete(`/sms/devices/${deviceId}`);
      await loadDevices(); // Refresh list
    } catch (error) {
      console.error('Error deactivating device:', error);
      alert(t('failedToDeactivateDevice'));
    }
  };

  const repairDevice = async (device: SmsDevice) => {
    try {
      // Regenerar código de emparejamiento
      const response = await apiClient.post(`/sms/devices/${device.id}/regenerate-pairing`);
      setPairingInfo(response.pairingInfo);
      setShowAddDevice(true);
      setSelectedDevice(device);
    } catch (error) {
      console.error('Error re-pairing device:', error);
      alert(t('failedToRepairDevice') || 'Failed to regenerate pairing code');
    }
  };

  const reactivateDevice = async (deviceId: number) => {
    try {
      await apiClient.post(`/sms/devices/${deviceId}/reactivate`);
      await loadDevices();
    } catch (error) {
      console.error('Error reactivating device:', error);
      alert(t('failedToReactivateDevice') || 'Failed to reactivate device');
    }
  };

  const deleteDevicePermanently = async (deviceId: number) => {
    if (!confirm(t('confirmDeleteDevice') || '¿Eliminar este dispositivo permanentemente? Esta acción no se puede deshacer.')) return;
    
    try {
      // TODO: Agregar endpoint DELETE /api/sms/devices/:id/delete
      await apiClient.post(`/sms/devices/${deviceId}/delete`);
      await loadDevices();
    } catch (error) {
      console.error('Error deleting device:', error);
      alert(t('failedToDeleteDevice') || 'Failed to delete device');
    }
  };

  const getStatusBadgeColor = (isActive: boolean, isPaired: boolean) => {
    if (isActive && isPaired) {
      return 'bg-green-100 text-green-800';
    } else if (!isPaired) {
      return 'bg-yellow-100 text-yellow-800';
    } else {
      return 'bg-red-100 text-red-800';
    }
  };

  const getStatusText = (isActive: boolean, isPaired: boolean) => {
    console.log('getStatusText called with:', { isActive, isPaired });
    if (isActive && isPaired) {
      return t('active');
    } else if (!isPaired && isActive) {
      return t('pendingPairing');
    } else if (!isPaired && !isActive) {
      return t('unpaired') || 'Desemparejado';
    } else {
      return t('inactive');
    }
  };

  const getMessageStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const startTour = () => {
    const steps = [];

    // Header
    steps.push({
      element: '#sms-page-header',
      popover: {
        title: t('sms'),
        description: t('tourSmsPageDesc'),
        side: 'bottom',
        align: 'start',
      },
    });

    steps.push({
      element: '#add-device-button',
      popover: {
        title: t('addDevice'),
        description: t('tourAddDeviceDesc'),
        side: 'left',
        align: 'start',
      },
    });

    // Devices section
    if (devices.length > 0) {
      steps.push({
        element: '#devices-section',
        popover: {
          title: t('yourDevices'),
          description: t('tourDevicesSectionDesc'),
          side: 'bottom',
          align: 'start',
        },
      });
    }

    // Messages section
    steps.push({
      element: '#messages-section',
      popover: {
        title: t('messageHistory'),
        description: t('tourMessagesSectionDesc'),
        side: 'top',
        align: 'start',
      },
    });

    steps.push({
      element: '#search-filters',
      popover: {
        title: t('searchFilters'),
        description: t('tourSearchFiltersDesc'),
        side: 'bottom',
        align: 'start',
      },
    });

    if (messages.length > 0) {
      steps.push({
        element: '#messages-list',
        popover: {
          title: t('messagesList'),
          description: t('tourMessagesListDesc'),
          side: 'top',
          align: 'start',
        },
      });
    }

    driver({
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      steps,
    }).drive();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div id="sms-page-header" className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Smartphone className="h-8 w-8" />
              {t('sms')}
            </h1>
            <p className="mt-2 text-gray-600">
              {t('manageSmsDevices')}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {t('onlyOneDeviceAllowed')}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={startTour}
              variant="outline"
              className="flex items-center gap-2"
              title={t('startTour')}
            >
              <HelpCircle className="h-5 w-5" />
            </Button>
            <Button
              id="add-device-button"
              onClick={() => setShowAddDevice(!showAddDevice)}
              className="flex items-center gap-2"
              disabled={hasAnyDevice}
            >
              <Plus className="h-5 w-5" />
              {hasAnyDevice ? (t('deviceAlreadyRegistered')) : t('addDevice')}
            </Button>
          </div>
        </div>

        {/* Add Device Form / QR Pairing */}
        {showAddDevice && (
          <Card className="mb-6 p-6">
            {!pairingInfo ? (
              <>
                <h2 className="mb-4 text-xl font-semibold">{t('registerNewDevice')}</h2>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('deviceName')}
                    </label>
                    <input
                      type="text"
                      className="w-full rounded-md border border-gray-300 px-3 py-2"
                      placeholder={t('deviceNamePlaceholder') || 'e.g., Samsung Galaxy'}
                      value={newDevice.deviceName}
                      onChange={(e) => setNewDevice({ ...newDevice, deviceName: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('phoneNumber')}
                    </label>
                    <input
                      type="text"
                      className="w-full rounded-md border border-gray-300 px-3 py-2"
                      placeholder={t('phoneNumberPlaceholder') || '+1234567890'}
                      value={newDevice.phoneNumber}
                      onChange={(e) => setNewDevice({ ...newDevice, phoneNumber: e.target.value })}
                    />
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button onClick={registerDevice}>{t('register')}</Button>
                  <Button variant="outline" onClick={closeAddDeviceModal}>
                    {t('cancel')}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <h2 className="mb-2 text-xl font-semibold flex items-center gap-2">
                  <QrCode className="h-6 w-6" />
                  {t('pairYourDevice')}
                </h2>
                <p className="text-sm text-gray-600 mb-6">
                  {t('scanQrCodeWithMobileApp')}
                </p>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* QR Code */}
                  <div className="flex flex-col items-center">
                    <div className="bg-white p-4 rounded-lg border-2 border-gray-200 shadow-sm">
                      <img 
                        src={pairingInfo.qrCode} 
                        alt="Pairing QR Code" 
                        className="w-64 h-64"
                      />
                    </div>
                    <p className="mt-3 text-xs text-gray-500">
                      {t('qrCodeExpiresAt', { time: new Date(pairingInfo.expiresAt).toLocaleTimeString() })}
                    </p>
                  </div>

                  {/* Manual Code Entry */}
                  <div className="flex flex-col justify-center">
                    <h3 className="font-semibold mb-3">{t('orEnterManually')}</h3>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('pairingCode')}
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          readOnly
                          value={pairingInfo.code}
                          className="flex-1 rounded-md border border-gray-300 px-3 py-2 font-mono text-2xl text-center tracking-widest bg-gray-50"
                        />
                        <Button
                          variant="outline"
                          onClick={copyPairingCode}
                          className="flex items-center gap-2"
                        >
                          {copiedCode ? (
                            <>
                              <Check className="h-4 w-4" />
                              {t('copied')}
                            </>
                          ) : (
                            <>
                              <Copy className="h-4 w-4" />
                              {t('copy')}
                            </>
                          )}
                        </Button>
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('backendUrl')}
                      </label>
                      <input
                        type="text"
                        readOnly
                        value={pairingInfo.backendUrl}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 bg-gray-50 text-sm"
                      />
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-sm">
                      <h4 className="font-semibold text-blue-900 mb-2">{t('instructions')}</h4>
                      <ol className="list-decimal list-inside space-y-1 text-blue-800">
                        <li>{t('downloadSmsApp')}</li>
                        <li>{t('openAppAndScanQr')}</li>
                        <li>{t('waitForConfirmation')}</li>
                      </ol>
                      <div className="mt-3">
                        <a href="/apk/sms24.apk" download="sms24.apk">
                          <Button size="sm" className="w-full flex items-center justify-center gap-2">
                            <Download className="h-4 w-4" />
                            {t('downloadApp') || 'Descargar App SMS'}
                          </Button>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <Button onClick={closeAddDeviceModal}>
                    {t('done')}
                  </Button>
                </div>
              </>
            )}
          </Card>
        )}

        {/* Devices Grid */}
        <div id="devices-section" className="mb-6">
          <h2 className="mb-4 text-xl font-semibold">{t('yourDevices')}</h2>
          {devices.length === 0 ? (
            <Card className="p-8 text-center">
              <Smartphone className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-gray-600">{t('noDevicesYet')}</p>
              <p className="text-sm text-gray-500">
                {t('addDeviceToStartSending')}
              </p>
              <div className="mt-4">
                <a href="/apk/sms24.apk" download="sms24.apk">
                  <Button className="flex items-center gap-2">
                    <Download className="h-5 w-5" />
                    {t('downloadApp') || 'Descargar App SMS'}
                  </Button>
                </a>
              </div>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {devices.map((device) => (
                <Card
                  key={device.id}
                  className={`p-4 transition-all ${
                    selectedDevice?.id === device.id
                      ? 'ring-2 ring-indigo-500'
                      : 'hover:shadow-md'
                  }`}
                  onClick={() => setSelectedDevice(device)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{device.deviceName}</h3>
                        {device.isActive && device.isPaired && (
                          <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" title={t('defaultDevice')} />
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{device.phoneNumber}</p>
                      <p className="text-xs text-gray-500 mt-1">{device.deviceId}</p>
                      {device.platform && (
                        <p className="text-xs text-gray-400 mt-1">
                          {device.platform.charAt(0).toUpperCase() + device.platform.slice(1)}
                        </p>
                      )}
                    </div>
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-medium whitespace-nowrap ${getStatusBadgeColor(
                        device.isActive,
                        device.isPaired || false
                      )}`}
                    >
                      {getStatusText(device.isActive, device.isPaired || false)}
                    </span>
                  </div>

                  {device.lastSeen && (
                    <p className="text-xs text-gray-500 mb-3">
                      {t('lastSeen')}: {new Date(device.lastSeen).toLocaleString()}
                    </p>
                  )}

                  <div className="flex gap-2">
                    {/* Dispositivo activo y emparejado */}
                    {device.isActive && device.isPaired && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          deactivateDevice(device.id);
                        }}
                      >
                        {t('deactivate')}
                      </Button>
                    )}

                    {/* Dispositivo NO emparejado - Permitir re-emparejar */}
                    {!device.isPaired && (
                      <>
                        <Button
                          size="sm"
                          className="flex-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            repairDevice(device);
                          }}
                        >
                          {t('repair') || 'Re-emparejar'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteDevicePermanently(device.id);
                          }}
                        >
                          {t('delete')}
                        </Button>
                      </>
                    )}

                    {/* Dispositivo inactivo pero emparejado */}
                    {!device.isActive && device.isPaired && (
                      <>
                        <Button
                          size="sm"
                          className="flex-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            reactivateDevice(device.id);
                          }}
                        >
                          {t('activate') || 'Activar'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteDevicePermanently(device.id);
                          }}
                        >
                          {t('delete')}
                        </Button>
                      </>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Messages History */}
        <div id="messages-section">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold">{t('messageHistory')}</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadMessages()}
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              {t('refresh')}
            </Button>
          </div>

          {/* Search Filters */}
          <Card id="search-filters" className="mb-4 p-4">
            <h3 className="text-sm font-semibold mb-3">{t('searchFilters') || 'Search Filters'}</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t('searchMessage')}
                </label>
                <input
                  type="text"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  placeholder={t('searchMessagePlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t('phoneNumber')}
                </label>
                <input
                  type="text"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  placeholder="+1234567890"
                  value={searchPhone}
                  onChange={(e) => setSearchPhone(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t('startDate')}
                </label>
                <input
                  type="date"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t('endDate')}
                </label>
                <input
                  type="date"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <Button size="sm" onClick={handleSearch}>
                {t('search')}
              </Button>
              <Button size="sm" variant="outline" onClick={handleClearSearch}>
                {t('clear')}
              </Button>
            </div>
          </Card>

          {loading ? (
            <Card className="p-8 text-center">
              <RefreshCw className="mx-auto h-8 w-8 animate-spin text-gray-400" />
              <p className="mt-2 text-gray-600">{t('loadingMessages')}</p>
            </Card>
          ) : messages.length === 0 ? (
            <Card className="p-8 text-center">
              <Inbox className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-gray-600">{t('noMessagesYet')}</p>
            </Card>
          ) : (
            <Card id="messages-list" className="divide-y">
              {messages.map((message) => (
                <div key={message.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {message.direction === 'outbound' ? (
                          <Send className="h-4 w-4 text-blue-600" />
                        ) : (
                          <Inbox className="h-4 w-4 text-green-600" />
                        )}
                        <span className="font-medium text-gray-900">
                          {message.direction === 'outbound' ? t('to') : t('from')}: {message.recipient}
                        </span>
                        <span
                          className={`ml-auto rounded-full px-2 py-1 text-xs font-medium ${getMessageStatusBadgeColor(
                            message.status
                          )}`}
                        >
                          {message.status}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-gray-700">{message.content}</p>
                      <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(message.createdAt).toLocaleString()}
                        </span>
                        {message.bitrixMessageId && (
                          <span className="text-blue-600">{t('bitrix24Message', { id: message.bitrixMessageId })}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </Card>
          )}

          {/* Pagination Controls */}
          {!loading && messages.length > 0 && totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {t('showingResults', { 
                  from: (currentPage - 1) * pageSize + 1,
                  to: Math.min(currentPage * pageSize, totalMessages),
                  total: totalMessages 
                }) || `Showing ${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, totalMessages)} of ${totalMessages}`}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadMessages(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  {t('previous') || 'Previous'}
                </Button>
                <div className="flex items-center px-4 text-sm font-medium">
                  {t('pageOfPages', { page: currentPage, totalPages }) || `Page ${currentPage} of ${totalPages}`}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadMessages(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  {t('next') || 'Next'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
